import { type FC, useState, useEffect } from "react";
import type { FileEntry } from "@bloomstack/apis/files";
import { FileIcon, FileTextIcon, ImageIcon, VideoIcon, Music, BracesIcon } from "lucide-react";
import { fileDb } from "@/db/fileDatabase";
import { useTabs } from "@/contexts/tabsContext";
import { ImageViewer } from "./viewers/ImageViewer";
import { TextViewer } from "./viewers/TextViewer";
import { PdfViewer } from "./viewers/PdfViewer";
import { VideoViewer } from "./viewers/VideoViewer";
import { AudioViewer } from "./viewers/AudioViewer";
import { JsonViewer } from "./viewers/JsonViewer";
import { GenericViewer } from "./viewers/GenericViewer";
import { useFilePreviewReload } from "./useFilePreviewReload";
import { FileMetadataPanel } from "./FileMetadataPanel";

const isJson = (mime: string, fileName: string): boolean =>
  mime === "application/json" || /\.json$/i.test(fileName);

type FilePreviewTabProps = {
  fileId: string;
  tabId: string;
};

export const FilePreviewTab: FC<FilePreviewTabProps> = ({ fileId, tabId }) => {
  const tabs = useTabs();
  const [file, setFile] = useState<FileEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reloadKey = useFilePreviewReload(file?.roomId);

  // Fetch file from IndexedDB
  useEffect(() => {
    let cancelled = false;

    fileDb.files.get(fileId).then((entry) => {
      if (cancelled) return;
      if (entry) {
        setFile(entry);
      } else {
        setError("File not found");
      }
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setError("Failed to load file");
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [fileId]);

  // Update tab metadata once file is loaded
  useEffect(() => {
    if (!file) return;

    let icon: React.ReactNode;
    if (file.fileMime.startsWith("image/")) {
      icon = <ImageIcon className="w-3.5 h-3.5" />;
    } else if (file.fileMime === "application/pdf") {
      icon = <FileTextIcon className="w-3.5 h-3.5" />;
    } else if (file.fileMime.startsWith("video/")) {
      icon = <VideoIcon className="w-3.5 h-3.5" />;
    } else if (file.fileMime.startsWith("audio/")) {
      icon = <Music className="w-3.5 h-3.5" />;
    } else if (isJson(file.fileMime, file.fileName)) {
      icon = <BracesIcon className="w-3.5 h-3.5" />;
    } else if (file.fileMime.startsWith("text/")) {
      icon = <FileTextIcon className="w-3.5 h-3.5" />;
    } else {
      icon = <FileIcon className="w-3.5 h-3.5" />;
    }

    tabs.setTabMetadata(tabId, { title: file.fileName, icon });
  }, [file, tabId, tabs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-500" />
      </div>
    );
  }

  if (error || !file) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-red-500">{error ?? "Unknown error"}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0">
        {file.fileMime.startsWith("image/") ? (
          <ImageViewer fileId={file.id} key={reloadKey} />
        ) : file.fileMime === "application/pdf" ? (
          <PdfViewer fileId={file.id} fileName={file.fileName} key={reloadKey} />
        ) : file.fileMime.startsWith("video/") ? (
          <VideoViewer fileId={file.id} key={reloadKey} />
        ) : file.fileMime.startsWith("audio/") ? (
          <AudioViewer fileId={file.id} fileName={file.fileName} key={reloadKey} />
        ) : isJson(file.fileMime, file.fileName) ? (
          <JsonViewer fileId={file.id} key={reloadKey} />
        ) : file.fileMime.startsWith("text/") ? (
          <TextViewer fileId={file.id} fileName={file.fileName} key={reloadKey} />
        ) : (
          <GenericViewer fileName={file.fileName} mime={file.fileMime} key={reloadKey} />
        )}
      </div>
      <FileMetadataPanel file={file} />
    </div>
  );
};
